import type { ContinueCard } from "@anisync/types";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { getContinueWatching } from "../lib/api";
import { formatClock } from "../lib/format";
import { fadeIn, stagger } from "../lib/motion";

/**
 * Półka „Kontynuuj oglądanie".
 *
 * Zawiera zarówno odcinki przerwane w połowie, jak i następny po ostatnio
 * ukończonym — serwer zwraca jedno i drugie w tej samej liście, bo dla widza to
 * ta sama potrzeba: „co teraz włączyć".
 */
export function ContinueRail({ enabled }: { enabled: boolean }) {
  const [items, setItems] = useState<ContinueCard[] | null>(null);

  useEffect(() => {
    if (!enabled) {
      setItems(null);
      return;
    }
    let cancelled = false;
    getContinueWatching(12)
      .then((data) => {
        if (!cancelled) setItems(data.items);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Pusta półka nie ma czego komunikować — u nowego konta zajmowałaby miejsce
  // nagłówkiem bez treści.
  if (!enabled || items === null || items.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">Kontynuuj oglądanie</h2>
      {/* Kolumn tyle, ile się mieści (`tile-grid`). Sztywne progi kończyły się na
          sześciu, więc na monitorze 4K kafel miał ponad 400 pikseli szerokości. */}
      <ul className="tile-grid gap-4 [--tile-min:11rem]">
        {items.map((card, i) => (
          <li
            key={`${card.titleId}-${card.episodeNumber}`}
            className="enter-rise"
            style={stagger(i)}
          >
            <ContinueTile card={card} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function ContinueTile({ card }: { card: ContinueCard }) {
  const name = card.title.titleEnglish ?? card.title.titleRomaji;
  const percent = Math.round(Math.min(1, Math.max(0, card.fraction)) * 100);

  // Materiał gotowy na dysku otwieramy wprost w odtwarzaczu; w przeciwnym razie
  // prowadzimy na stronę tytułu, gdzie start odcinka uruchamia pobieranie
  // i pokazuje jego postęp.
  const to =
    card.cachedMediaFileId !== null
      ? `/odtwarzacz/${card.cachedMediaFileId}?tytul=${card.titleId}&odcinek=${card.episodeNumber}`
      : `/tytul/${card.titleId}`;

  return (
    <Link
      to={to}
      className="focus-ring-own group flex flex-col gap-2 rounded-xl border border-border bg-card p-3 outline-none transition-all duration-200 ease-tv hover:border-primary/40 hover:bg-secondary/40 focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50"
    >
      <div className="overflow-hidden rounded-lg bg-secondary">
        {card.title.coverUrl !== null ? (
          <img
            {...fadeIn}
            src={card.title.coverUrl}
            alt=""
            loading="lazy"
            className="aspect-[2/3] w-full object-cover"
          />
        ) : (
          <div className="aspect-[2/3] w-full" />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <span className="line-clamp-2 text-sm font-medium">{name}</span>
        <span className="text-xs text-muted-foreground">
          Odcinek {card.episodeNumber}
          {card.positionSec > 0 ? ` — od ${formatClock(card.positionSec)}` : ""}
        </span>

        {/* Pasek rysujemy tylko dla odcinków rozpoczętych: przy zerze byłby
            pustą kreską sugerującą, że coś się nie wczytało.

            Ukryty dla czytników ekranu, bo to wyłącznie wizualne powtórzenie
            pozycji, którą wiersz obok podaje już słowami („od 12:34"). Rola
            progressbar dodawałaby drugi, mniej precyzyjny odczyt tej samej
            informacji. */}
        {card.positionSec > 0 && (
          <div aria-hidden="true" className="h-1 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full bg-gradient-to-r from-primary to-accent"
              style={{ width: `${percent}%` }}
            />
          </div>
        )}
      </div>
    </Link>
  );
}
